/**
 * Kleine Werkzeuge für Bilder im Chat. (19.10.2026)
 *
 * - `hatTransparenz`: erkennt durchsichtige Bilder (PNG mit Alpha), damit sie eine abgesetzte Bildfläche bekommen.
 * - `makeDownloadButton`: der Herunterladen-Knopf neben dem Teilen-Knopf. Das Teilen selbst bleibt `shareMedia`
 *   (lib/share.ts, dieselbe Funktion wie in der Lightbox): Datei wenn das Gerät es kann, sonst Adresse.
 *
 * iOS: In Safari lädt `<a download>` bei Dateien derselben Herkunft; die App-Ansicht (WKWebView) tut dabei nichts. Dort
 * bleibt das Teilen-Blatt mit Datei („In Dateien sichern“) der Weg — darum öffnet der Knopf ohne `download`-Unterstützung
 * genau dieses Blatt (`shareMedia`).
 */
import { shareMedia } from "./share.js";
import { zeichen } from "./zeichen.js";

/**
 * Hat das Bild durchsichtige Stellen? Probe an einer kleinen Kopie auf einer Zeichenfläche (32 Punkte reichen für
 * Ränder und Fläche). Scheitert die Probe (fremde Herkunft, keine Zeichenfläche), gilt das Bild als undurchsichtig.
 */
export function hatTransparenz(img: HTMLImageElement): boolean {
  const kante = 32;
  try {
    const c = document.createElement("canvas");
    c.width = kante;
    c.height = kante;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0, kante, kante);
    const d = ctx.getImageData(0, 0, kante, kante).data;
    for (let i = 3; i < d.length; i += 4) if (d[i] < 250) return true;
  } catch {
    /* Probe nicht möglich — dann eben ohne abgesetzte Fläche */
  }
  return false;
}

/** Dateiname zum Speichern: der Name aus der Adresse, sonst „bild.png“; eine fehlende Endung wird ergänzt. */
export function bildDateiname(url: string): string {
  const roh = url.split("?")[0].split("#")[0].split("/").pop() ?? "";
  let name = "";
  try {
    name = decodeURIComponent(roh);
  } catch {
    name = roh;
  }
  if (!name) return "bild.png";
  return /\.[a-z0-9]{2,5}$/i.test(name) ? name : `${name}.png`;
}

/**
 * Ein Herunterladen-Link ohne `download`-Unterstützung (WKWebView) öffnet statt eines Links, der nichts speichert,
 * das Teilen-Blatt mit Datei. Liest Adresse und Namen beim Tipp — der Link darf also später umgehängt werden.
 */
export function bindeDownloadFallback(a: HTMLAnchorElement): void {
  a.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if ("download" in HTMLAnchorElement.prototype) return;
    ev.preventDefault();
    const url = a.getAttribute("href") ?? "";
    void shareMedia({ url, title: a.download, dateiname: a.download });
  });
}

/** Herunterladen-Knopf: ein Link mit `download` (PNG-Transparenz bleibt unverändert, es wird die Originaldatei geladen). */
export function makeDownloadButton(url: string, name: string = bildDateiname(url)): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = "bubble-share bubble-download";
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  a.setAttribute("aria-label", "Herunterladen");
  a.title = "Herunterladen";
  a.innerHTML = zeichen("herunterladen", 18);
  bindeDownloadFallback(a);
  return a;
}
